/**
 * i18n — language dictionary + t() lookup with fallback.
 *
 * Default language is English ('en'). Add a language by extending the
 * `defaultLabels` object below, or pass `config.labels` at init time to
 * extend / override per-host.
 *
 * Lookup rules:
 *   1. Look up labels[language][key]
 *   2. If missing → fall back to labels.en[key]
 *   3. If still missing → return the key itself (so missing strings are visible)
 */

export const defaultLabels = {
  en: {
    // Header
    header_suffix: 'travel assistant',
    status_online: 'Online',

    // Composer
    placeholder_first: 'Ask a question…',
    placeholder_reply: 'Reply…',
    send: 'Send',
    new_chat: 'New chat',
    close: 'Close',
    enter_to_send: 'Press Enter to send',
    shift_enter_newline: 'Shift+Enter for newline',

    // Empty state
    empty_title: 'How can I help you plan?',
    empty_subtitle: 'Ask about places to visit, things to do, or itineraries.',

    // Streaming / loading
    loading_response: 'Loading response',
    skeleton_label: 'Assistant is preparing a response',
    error_connection: 'A connection error occurred.',
    retry: 'Retry',

    // Map
    map_available: 'map available',
    map_open: 'Open map',
    map_close: 'Close map',
    map_loading: 'Loading map…',
    map_error: 'Couldn’t load the map.',
    map_retry: 'Retry',
    map_back_to_chat: 'Back to chat',
    blocks_available: 'suggestions available',
    show_on_map: 'Show on map',
    close_panel: 'Close panel',
    loading: 'Loading…',
    photos_by_pexels: 'Photos by Pexels',

    // Result cards
    show_on_site: 'Show on site',
    read_more: 'Read more',
    show_less: 'Show less',
    website: 'Website',
    event_website: 'Event Website',
    book_now: 'Book now',
    get_tickets: 'Get Tickets',
    get_offer: 'Get offer',
    read_article: 'Read article',
    operating_hours: 'Opening hours',
    organized_by: 'Organized by {name}',
    offered_by: 'Offered by {name}',
    valid_from: 'Valid from {date}',
    valid_until: 'Valid until {date}',
    type_listing: 'Listing',
    type_event: 'Event',
    type_article: 'Article',
    type_offer: 'Offer',

    // Local expert tips
    tips_available: 'local tips available',
    tips_open: 'Open local expert tips',
    tips_close: 'Close local expert tips',
    tips_loading: 'Loading local expert tips…',
    tips_empty: 'No local expert tips are available for this answer.',
    tips_card_title: 'What locals are saying',
    tips_card_subtitle: 'Based on vetted local reviews',
    tips_card_footer: 'Summarized by AI from vetted local reviews',

    // Author labels
    author_user: 'You',

    // Suggestions
    suggestion_things_to_do: 'What can I do this weekend?',
    suggestion_three_day: 'Plan a 3-day itinerary',
    suggestion_kid_friendly: 'Kid-friendly activities',

    // Share
    share: 'Share',
    share_link_title: 'Share this response',
    share_link_description: 'Copy the link below and send it to anyone.',
    copy: 'Copy',
    copied: '✓ Copied!',
    shared_view_message: 'You are viewing a shared response.',
    start_new_chat: 'Start a new chat',
  },
  es: {
    // Header
    header_suffix: 'asistente de viajes',
    status_online: 'En línea',

    // Composer
    placeholder_first: 'Haz una pregunta…',
    placeholder_reply: 'Responder…',
    send: 'Enviar',
    new_chat: 'Nueva conversación',
    close: 'Cerrar',
    enter_to_send: 'Presiona Enter para enviar',
    shift_enter_newline: 'Shift+Enter para nueva línea',

    // Empty state
    empty_title: '¿Cómo puedo ayudarte a planificar?',
    empty_subtitle: 'Pregunta sobre lugares para visitar, cosas que hacer o itinerarios.',

    // Streaming / loading
    loading_response: 'Cargando respuesta',
    skeleton_label: 'El asistente está preparando una respuesta',
    error_connection: 'Ocurrió un error de conexión.',
    retry: 'Reintentar',

    // Map
    map_available: 'mapa disponible',
    map_open: 'Abrir mapa',
    map_close: 'Cerrar mapa',
    map_loading: 'Cargando mapa…',
    map_error: 'No se pudo cargar el mapa.',
    map_retry: 'Reintentar',
    map_back_to_chat: 'Volver al chat',
    blocks_available: 'sugerencias disponibles',
    show_on_map: 'Mostrar en el mapa',
    close_panel: 'Cerrar panel',
    loading: 'Cargando…',
    photos_by_pexels: 'Fotos de Pexels',

    // Result cards
    show_on_site: 'Ver en el sitio',
    read_more: 'Leer más',
    show_less: 'Mostrar menos',
    website: 'Sitio web',
    event_website: 'Sitio web del evento',
    book_now: 'Reservar',
    get_tickets: 'Comprar entradas',
    get_offer: 'Ver oferta',
    read_article: 'Leer artículo',
    operating_hours: 'Horario',
    organized_by: 'Organizado por {name}',
    offered_by: 'Ofrecido por {name}',
    valid_from: 'Válido desde el {date}',
    valid_until: 'Válido hasta el {date}',
    type_listing: 'Lugar',
    type_event: 'Evento',
    type_article: 'Artículo',
    type_offer: 'Oferta',

    // Local expert tips
    tips_available: 'consejos locales disponibles',
    tips_open: 'Abrir consejos de expertos locales',
    tips_close: 'Cerrar consejos de expertos locales',
    tips_loading: 'Cargando consejos de expertos locales…',
    tips_empty: 'No hay consejos de expertos locales para esta respuesta.',
    tips_card_title: 'Lo que dicen los locales',
    tips_card_subtitle: 'Basado en reseñas locales verificadas',
    tips_card_footer: 'Resumido por IA a partir de reseñas locales verificadas',

    // Author labels
    author_user: 'Tú',

    // Suggestions
    suggestion_things_to_do: '¿Qué puedo hacer este fin de semana?',
    suggestion_three_day: 'Planificar un itinerario de 3 días',
    suggestion_kid_friendly: 'Actividades ideales para niños',

    // Share
    share: 'Compartir',
    share_link_title: 'Compartir esta respuesta',
    share_link_description: 'Copia el enlace y envíalo a quien quieras.',
    copy: 'Copiar',
    copied: '✓ ¡Copiado!',
    shared_view_message: 'Estás viendo una respuesta compartida.',
    start_new_chat: 'Iniciar un nuevo chat',
  },
};

/**
 * Build a t() translator bound to the active language.
 * @param {string|undefined} language  e.g. 'en', 'es', 'de'
 * @param {Record<string, Record<string,string>>} [hostLabels]  optional overrides from config.labels
 */
export function makeT(language, hostLabels) {
  const lang = (language && typeof language === 'string') ? language : 'en';

  // Merge defaults with host-supplied labels (host overrides default)
  const merged = {};
  for (const [code, dict] of Object.entries(defaultLabels)) {
    merged[code] = { ...dict };
  }
  if (hostLabels && typeof hostLabels === 'object') {
    for (const [code, dict] of Object.entries(hostLabels)) {
      merged[code] = { ...(merged[code] || {}), ...dict };
    }
  }

  return function t(key) {
    const direct = merged[lang]?.[key];
    if (direct !== undefined) return direct;
    const fallback = merged.en?.[key];
    if (fallback !== undefined) return fallback;
    return key; // last-resort: surface the missing key so it can be fixed
  };
}
